import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Copy, Users, Check, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { listClassGroups, createClassGroup, getClassStudents, listCourses } from '../../services/studioApi';
import UnifiedHeader from '../../components/UnifiedHeader';
import './Studio.css';

export function ClassManagementTab() {
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tTeacher = t.teacher || {};

  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCourseId, setNewCourseId] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg, type = '') => {
    setToast({ msg, type });
    setTimeout(() => setToast(''), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([listClassGroups(), listCourses()]);
      setGroups(g);
      setCourses(c);
      if (c.length > 0) setNewCourseId(c[0].id);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim() || !newCourseId) return;
    try {
      await createClassGroup({ name: newName.trim(), course_id: newCourseId });
      showToast(tTeacher.classCreated || 'Class created!', 'success');
      setShowCreate(false);
      setNewName('');
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const toggleStudents = async (groupId) => {
    if (expandedGroup === groupId) { setExpandedGroup(null); return; }
    setExpandedGroup(groupId);
    if (!students[groupId]) {
      const s = await getClassStudents(groupId).catch(() => []);
      setStudents(prev => ({ ...prev, [groupId]: s }));
    }
  };

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
    showToast(tTeacher.codeCopied || 'Invite code copied!', 'success');
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} /> {tTeacher.createClass || 'Create Class'}
        </button>
      </div>

      {showCreate && (
        <div className="studio-card" style={{ marginBottom: '1.25rem', border: '2px solid #5B35A0' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700 }}>
            {tTeacher.newClass || 'New Class Group'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{tTeacher.className || 'Class Name'}</label>
              <input className="form-input" value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder={tTeacher.classNamePlaceholder || 'e.g. Physics 2025'} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{tTeacher.course || 'Course'}</label>
              <select className="form-select" value={newCourseId}
                onChange={e => setNewCourseId(e.target.value)}>
                {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => setShowCreate(false)}>
              <X size={14} /> {t.cancel || 'Cancel'}
            </button>
            <button className="btn-primary" onClick={handleCreate} disabled={!newName.trim()}>
              <Check size={14} /> {t.create || 'Create'}
            </button>
          </div>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Users size={48} /></div>
          <h3>{tTeacher.noClasses || 'No classes yet'}</h3>
          <p>{tTeacher.createClassDesc || 'Create a class and share the invite code with students'}</p>
        </div>
      ) : (
        groups.map(g => (
          <div key={g.id} className="studio-card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ margin: '0 0 0.25rem', fontWeight: 700, color: '#1a1a2e' }}>{g.name}</h3>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#888' }}>
                  {g.course_title} · {g.student_count || 0} {tTeacher.students || 'students'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#aaa', marginBottom: '0.2rem' }}>
                    {tTeacher.inviteCode || 'Invite Code'}
                  </div>
                  <div className="invite-code" style={{ fontSize: '1.1rem', letterSpacing: 3, padding: '0.4rem 0.75rem' }}>
                    {g.invite_code}
                    <button className="btn-icon" onClick={() => copyCode(g.invite_code, g.id)} title="Copy">
                      {copiedCode === g.id ? <Check size={14} color="#4caf50" /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
                <button className="btn-secondary" onClick={() => toggleStudents(g.id)}>
                  <Users size={14} />
                  {expandedGroup === g.id ? (tTeacher.hideStudents || 'Hide') : (tTeacher.viewStudents || 'Students')}
                </button>
              </div>
            </div>

            {expandedGroup === g.id && (
              <div style={{ marginTop: '1rem', borderTop: '1px solid #f0f0f0', paddingTop: '1rem' }}>
                {!students[g.id] || students[g.id].length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
                    {tTeacher.noStudentsEnrolled || 'No students enrolled yet. Share the invite code above.'}
                  </p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <th style={{ textAlign: 'left', padding: '0.4rem', color: '#666', fontWeight: 600 }}>{tTeacher.studentName || 'Name'}</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem', color: '#666', fontWeight: 600 }}>{tTeacher.email || 'Email'}</th>
                        <th style={{ textAlign: 'left', padding: '0.4rem', color: '#666', fontWeight: 600 }}>{tTeacher.enrolledAt || 'Enrolled'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students[g.id].map(s => (
                        <tr key={s.user_id} style={{ borderBottom: '1px solid #fafafa' }}>
                          <td style={{ padding: '0.5rem 0.4rem', fontWeight: 500 }}>{s.full_name}</td>
                          <td style={{ padding: '0.5rem 0.4rem', color: '#888' }}>{s.email}</td>
                          <td style={{ padding: '0.5rem 0.4rem', color: '#aaa', fontSize: '0.78rem' }}>
                            {s.enrolled_at ? new Date(s.enrolled_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))
      )}

      {toast && <div className={`studio-toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

export default function ClassManagement() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tTeacher = t.teacher || {};

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <div className="studio-subheader-left">
          <button className="studio-back-btn" onClick={() => navigate('/studio/teacher')}>
            <ArrowLeft size={15} /> {t.back || 'Back'}
          </button>
          <h1 className="studio-page-title">{tTeacher.classes || 'Classes'}</h1>
        </div>
      } />
      <div className="studio-main">
        <ClassManagementTab />
      </div>
    </div>
  );
}
